import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Heart, UserPlus, MessageCircle, Reply as ReplyIcon, Loader2 } from 'lucide-react';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';

const iconFor = (type) => {
  if (type === 'follow') return { Icon: UserPlus, cls: 'text-cyan-400 bg-cyan-500/15' };
  if (type === 'reaction') return { Icon: Heart, cls: 'text-rose-400 bg-rose-500/15' };
  if (type === 'reply') return { Icon: ReplyIcon, cls: 'text-fuchsia-400 bg-fuchsia-500/15' };
  return { Icon: MessageCircle, cls: 'text-amber-400 bg-amber-500/15' };
};

const NotificationBell = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, { withCredentials: true });
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { setItems([]); setUnread(0); return; }
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user]);

  const toggle = async () => {
    setOpen(v => {
      const next = !v;
      if (next && unread > 0) {
        axios.post(`${API}/notifications/read`, {}, { withCredentials: true }).then(() => setUnread(0)).catch(() => {});
      }
      if (next) load();
      return next;
    });
  };

  if (!user) return null;

  const text = (n) => {
    const name = n.from_user?.name || (lang === 'sv' ? 'Någon' : 'شخص ما');
    if (n.type === 'follow') return `${name} ${lang === 'sv' ? 'började följa dig' : 'بدأ متابعتك'}`;
    if (n.type === 'reaction') return `${name} ${lang === 'sv' ? 'reagerade' : 'تفاعل'} ${n.extra?.emoji || ''} ${lang === 'sv' ? 'på din video' : 'على فيديوهك'}`;
    if (n.type === 'reply') return `${name} ${lang === 'sv' ? 'svarade på din kommentar' : 'رد على تعليقك'}`;
    if (n.type === 'comment') return `${name} ${lang === 'sv' ? 'kommenterade din video' : 'علق على فيديوهك'}`;
    return name;
  };

  return (
    <div className="relative">
      <button ref={btnRef} onClick={toggle} onBlur={() => setTimeout(() => setOpen(false), 200)} className="relative w-10 h-10 rounded-full hover:bg-neutral-100 flex items-center justify-center transition-colors" aria-label="notifications">
        <Bell className="w-5 h-5 text-neutral-700" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 rtl:right-auto rtl:left-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 border-2 border-white text-white text-[10px] font-black flex items-center justify-center px-1">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute top-full mt-2 right-0 rtl:right-auto rtl:left-0 bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden w-[340px] max-h-[480px] flex flex-col z-50" onMouseDown={(e) => e.preventDefault()}>
          <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
            <div className="font-bold text-[14px] text-neutral-900">{lang === 'sv' ? 'Notifikationer' : 'الإشعارات'}</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center text-neutral-500 text-[13px]">{lang === 'sv' ? 'Inga notifikationer ännu' : 'لا توجد إشعارات بعد'}</div>
            ) : (
              items.map(n => {
                const { Icon, cls } = iconFor(n.type);
                const href = n.video_id ? `/v/${n.video_id}` : (n.from_user?.user_id ? `/u/${n.from_user.user_id}` : '#');
                return (
                  <a key={n.id} href={href} className={`flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-0 ${!n.is_read ? 'bg-cyan-50/40' : ''}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}>
                      {n.from_user?.picture ? <img src={n.from_user.picture} alt="" className="w-full h-full rounded-full object-cover" referrerPolicy="no-referrer" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-neutral-800 leading-snug">{text(n)}</div>
                      {n.extra?.text && <div className="mt-1 text-[11.5px] text-neutral-500 truncate">"{n.extra.text}"</div>}
                      <div className="mt-1 text-[10.5px] text-neutral-400">{new Date(n.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</div>
                    </div>
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0 mt-2" />}
                  </a>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
